import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import leaveService from "../../services/leaveService";
import { getErrorMessage } from "../../utils/errorHandler";
import FormCard from "../../components/ui/FormCard";
import FormSection from "../../components/ui/FormSection";
import FormActions from "../../components/ui/FormActions";
import Breadcrumb from "../../components/ui/Breadcrumb";
import "../../styles/FormCard.css";

const LeaveEdit = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    employee_id: "",
    leave_type_id: "",
    start_date: "",
    end_date: "",
    is_half_day: false,
    reason: "",
    status: "",
    manager_comment: "",
    hr_comment: "",
    attachment: null,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await leaveService.getById(id);
      const leave = response?.data || response;
      
      setFormData({
        employee_id: leave.employee_id || "",
        leave_type_id: leave.leave_type_id || "",
        start_date: leave.start_date ? leave.start_date.split('T')[0] : "",
        end_date: leave.end_date ? leave.end_date.split('T')[0] : "",
        is_half_day: leave.is_half_day || false,
        reason: leave.reason || "",
        status: leave.status || "",
        manager_comment: leave.manager_comment || "",
        hr_comment: leave.hr_comment || "",
        attachment: null,
      });
    } catch (err) {
      console.error("Error loading leave request:", err);
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (files ? files[0] : value)
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.start_date) newErrors.start_date = "La date de début est requise";
    if (!formData.end_date) newErrors.end_date = "La date de fin est requise";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSubmitting(true);
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'attachment' && formData[key]) {
          formDataToSend.append('attachment', formData[key]);
        } else if (formData[key] !== null && formData[key] !== "") {
          formDataToSend.append(key, formData[key]);
        }
      });

      await leaveService.update(id, formDataToSend);
      toast.success("Demande de congé mise à jour avec succès");
      navigate("/rh/leaves/history");
    } catch (err) {
      console.error("Error updating leave request:", err);
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/rh/leaves/history");
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: "RH", to: "/rh/dashboard" },
    { label: "Congés", to: "/rh/leaves" },
    { label: "Historique", to: "/rh/leaves/history" },
    { label: "Modifier la demande de congé" }
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Breadcrumb items={breadcrumbItems} />
      <FormCard title="Modifier la demande de congé" showBackButton onBack={handleCancel}>
        <form onSubmit={handleSubmit}>
          <FormSection title="Informations de la demande">
            <div className="form-group">
              <label>Employé</label>
              <input
                type="text"
                name="employee_id"
                value={formData.employee_id}
                disabled
                className="disabled"
              />
            </div>
            <div className="form-group">
              <label>Type de congé</label>
              <input
                type="text"
                name="leave_type_id"
                value={formData.leave_type_id}
                disabled
                className="disabled"
              />
            </div>
            <div className="form-group">
              <label>Date de début *</label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                className={errors.start_date ? "error" : ""}
              />
              {errors.start_date && <span className="error">{errors.start_date}</span>}
            </div>
            <div className="form-group">
              <label>Date de fin *</label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                className={errors.end_date ? "error" : ""}
              />
              {errors.end_date && <span className="error">{errors.end_date}</span>}
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="is_half_day"
                  checked={formData.is_half_day}
                  onChange={handleChange}
                />
                Demi-journée
              </label>
            </div>
            <div className="form-group">
              <label>Motif</label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                placeholder="Motif du congé..."
              />
            </div>
          </FormSection>

          <FormSection title="Statut et commentaires">
            <div className="form-group">
              <label>Statut</label>
              <input
                type="text"
                name="status"
                value={formData.status}
                disabled
                className="disabled"
              />
            </div>
            <div className="form-group">
              <label>Commentaire du manager</label>
              <textarea
                name="manager_comment"
                value={formData.manager_comment}
                onChange={handleChange}
                placeholder="Commentaire du manager..."
              />
            </div>
            <div className="form-group">
              <label>Commentaire RH</label>
              <textarea
                name="hr_comment"
                value={formData.hr_comment}
                onChange={handleChange}
                placeholder="Commentaire RH..."
              />
            </div>
          </FormSection>

          <FormSection title="Pièces jointes">
            <div className="form-group">
              <label>Ajouter une pièce jointe complémentaire</label>
              <input
                type="file"
                name="attachment"
                onChange={handleChange}
              />
            </div>
          </FormSection>

          <FormActions
            onCancel={handleCancel}
            onSave={handleSubmit}
            saveText="Enregistrer les modifications"
            isLoading={submitting}
          />
        </form>
      </FormCard>
    </div>
  );
};

export default LeaveEdit;

